import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { hasPermission, useClaims } from "@/hooks/use-claims";
import { useTranslation } from "@/i18n/use-translation";
import { apiError, showApiError, showApiErrorAs } from "@/lib/api-error";
import { formatDate } from "@/lib/format";
import { ImportUsersDialog } from "@/components/import-users-dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface Member {
  user_role_id: string;
  user_id: string;
  org_id: string;
  role_id: string;
  is_active: boolean;
  joined_at: string;
  email: string;
  full_name: string | null;
  last_sign_in_at: string | null;
  role_name: string;
  role_is_system: boolean;
  site_ids: string[] | null;
}

interface Role {
  id: string;
  name: string;
  is_system: boolean;
}

interface Site {
  id: string;
  name: string;
}

interface Invitation {
  id: string;
  org_id: string;
  email: string;
  role_id: string;
  site_ids: string[] | null;
  status: string;
  expires_at: string;
  created_at: string;
  role_name: string | null;
}

/** The page lists what `GET /members` answers, which needs `user:read` (POS2-1041). */
export default function SettingsUsersPage() {
  const { isPlatformAdmin, permissions } = useClaims();
  if (!isPlatformAdmin && !hasPermission(permissions, "user:read")) {
    return <Navigate to="/" replace />;
  }
  return <UsersPage />;
}

function UsersPage() {
  const { t } = useTranslation();
  const headers = useApiHeaders();

  const membersQuery = useQuery<Member[]>({
    queryKey: ["members"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/members`, { headers });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status}: ${text || res.statusText}`);
      }
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const rolesQuery = useQuery<Role[]>({
    queryKey: ["roles"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/invitations/roles`, { headers });
      if (!res.ok) throw new Error("Failed to load roles");
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const invitationsQuery = useQuery<Invitation[]>({
    queryKey: ["invitations"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/invitations`, { headers });
      if (!res.ok) throw new Error("Failed to load invitations");
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const sitesQuery = useQuery<Site[]>({
    queryKey: ["sites"],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/sites`, { headers });
      if (!res.ok) return [];
      return (await res.json()).data;
    },
    enabled: !!headers.Authorization.replace("Bearer ", ""),
  });

  const sites = useMemo(() => sitesQuery.data ?? [], [sitesQuery.data]);

  const roles = rolesQuery.data ?? [];
  const pendingInvitations = (invitationsQuery.data ?? []).filter((i) => i.status === "pending");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-medium">{t("settings.usersTitle")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("settings.usersDescription")}</p>
        </div>
        <div className="flex items-center gap-2">
          <ImportUsersDialog />
          <InviteDialog roles={roles} sites={sites} headers={headers} />
        </div>
      </div>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">{t("settings.tabMembers")}</TabsTrigger>
          <TabsTrigger value="invitations">
            {t("settings.tabInvitations")}
            {pendingInvitations.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-5 min-w-5 px-1">
                {pendingInvitations.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4">
          <MembersTable
            members={membersQuery.data ?? []}
            roles={roles}
            sites={sites}
            headers={headers}
            isLoading={membersQuery.isLoading}
            isError={membersQuery.isError}
            error={membersQuery.error}
          />
        </TabsContent>

        <TabsContent value="invitations" className="mt-4">
          <InvitationsTable
            invitations={invitationsQuery.data ?? []}
            sites={sites}
            isLoading={invitationsQuery.isLoading}
            headers={headers}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InviteDialog({
  roles,
  sites,
  headers,
}: {
  roles: Role[];
  sites: Site[];
  headers: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const selectedRole = roles.find((r) => r.id === roleId);
  const isSiteScoped = selectedRole?.name === "Site Manager" || selectedRole?.name === "Staff";

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);

    const payload: Record<string, unknown> = { email, role_id: roleId };
    if (isSiteScoped && siteIds.length > 0) {
      payload.site_ids = siteIds;
    }

    try {
      const res = await fetch(`${API_URL}/api/v1/invitations`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        throw await apiError(t, res);
      }

      const data = (await res.json()).data;
      if (data.email_sent) {
        toast.success(t("settings.invitationSent", { email }));
      } else {
        toast.warning(t("settings.invitationNoEmail"));
      }
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      setOpen(false);
      setEmail("");
      setRoleId("");
      setSiteIds([]);
    } catch (err) {
      showApiErrorAs(t("settings.inviteFailed"), err as Error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm">{t("settings.inviteMember")}</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("settings.inviteTitle")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleInvite} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-email" className="text-sm font-medium">
              {t("settings.emailAddress")}
            </label>
            <Input
              id="invite-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="invite-role" className="text-sm font-medium">
              {t("settings.role")}
            </label>
            <select
              id="invite-role"
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
                setSiteIds([]);
              }}
              required
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <option value="">{t("settings.selectRole")}</option>
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          {isSiteScoped && (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium">{t("settings.sites")}</legend>
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border p-2">
                {sites.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t("settings.noSitesAvailable")}</p>
                )}
                {sites.map((site) => (
                  <label key={site.id} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-gray-300"
                      checked={siteIds.includes(site.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSiteIds([...siteIds, site.id]);
                        } else {
                          setSiteIds(siteIds.filter((id) => id !== site.id));
                        }
                      }}
                    />
                    <span className="text-sm">{site.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <Button
            type="submit"
            disabled={submitting || !email || !roleId || (isSiteScoped && siteIds.length === 0)}
          >
            {submitting ? t("settings.sending") : t("settings.sendInvitation")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({
  member,
  roles,
  sites,
  headers,
}: {
  member: Member;
  roles: Role[];
  sites: Site[];
  headers: Record<string, string>;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [roleId, setRoleId] = useState(member.role_id);
  const [siteIds, setSiteIds] = useState<string[]>(member.site_ids ?? []);
  const [isActive, setIsActive] = useState(member.is_active);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const selectedRole = roles.find((r) => r.id === roleId);
  const isSiteScoped = selectedRole?.name === "Site Manager" || selectedRole?.name === "Staff";

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {};
      if (roleId !== member.role_id) payload.role_id = roleId;
      if (isActive !== member.is_active) payload.is_active = isActive;
      if (isSiteScoped) payload.site_ids = siteIds;
      else payload.site_ids = [];

      const res = await fetch(`${API_URL}/api/v1/members/${member.user_id}`, {
        method: "PATCH",
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw await apiError(t, res);
      queryClient.invalidateQueries({ queryKey: ["members"] });
      toast.success(t("settings.memberUpdated"));
      setOpen(false);
    } catch (err) {
      showApiErrorAs(t("settings.memberUpdateFailed"), err as Error);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          setRoleId(member.role_id);
          setSiteIds(member.site_ids ?? []);
          setIsActive(member.is_active);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="h-7 text-xs">
          {t("settings.edit")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t("settings.editMember", { name: member.full_name || member.email })}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSave} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`role-${member.user_id}`} className="text-sm font-medium">
              {t("settings.role")}
            </label>
            <select
              id={`role-${member.user_id}`}
              value={roleId}
              onChange={(e) => {
                setRoleId(e.target.value);
                setSiteIds([]);
              }}
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {roles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          {isSiteScoped && (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="text-sm font-medium">{t("settings.sites")}</legend>
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border p-2">
                {sites.length === 0 && (
                  <p className="text-xs text-muted-foreground">{t("settings.noSitesAvailable")}</p>
                )}
                {sites.map((site) => (
                  <label key={site.id} className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      className="size-4 rounded border-gray-300"
                      checked={siteIds.includes(site.id)}
                      onChange={(e) => {
                        if (e.target.checked) setSiteIds([...siteIds, site.id]);
                        else setSiteIds(siteIds.filter((id) => id !== site.id));
                      }}
                    />
                    <span className="text-sm">{site.name}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id={`active-${member.user_id}`}
              className="size-4 rounded border-gray-300"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <label htmlFor={`active-${member.user_id}`} className="text-sm font-medium">
              {t("settings.active")}
            </label>
          </div>
          <Button type="submit" disabled={submitting || (isSiteScoped && siteIds.length === 0)}>
            {submitting ? t("common.saving") : t("settings.saveChanges")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MembersTable({
  members,
  roles,
  sites,
  headers,
  isLoading,
  isError,
  error,
}: {
  members: Member[];
  roles: Role[];
  sites: Site[];
  headers: Record<string, string>;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}) {
  const { locale, t } = useTranslation();
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded border border-destructive/50 bg-destructive/10 p-3">
        <p className="text-sm font-medium text-destructive">{t("settings.loadMembersFailed")}</p>
        <p className="mt-1 font-mono text-xs text-destructive/80">
          {error instanceof Error ? error.message : t("settings.unknownError")}
        </p>
      </div>
    );
  }

  if (members.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
        <p className="text-sm">{t("settings.noMembers")}</p>
        <p className="text-xs">{t("settings.noMembersHint")}</p>
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("settings.columnName")}</TableHead>
          <TableHead>{t("settings.columnEmail")}</TableHead>
          <TableHead>{t("settings.columnRole")}</TableHead>
          <TableHead>{t("settings.columnSites")}</TableHead>
          <TableHead>{t("settings.columnStatus")}</TableHead>
          <TableHead>{t("settings.columnLastActive")}</TableHead>
          <TableHead className="text-right">{t("settings.columnActions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((member) => (
          <TableRow key={member.user_id}>
            <TableCell className="font-medium">{member.full_name ?? "—"}</TableCell>
            <TableCell className="text-sm text-muted-foreground">{member.email}</TableCell>
            <TableCell>
              <Badge variant="secondary">{member.role_name}</Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {member.site_ids && member.site_ids.length > 0
                ? member.site_ids
                    .map((sid) => sites.find((s) => s.id === sid)?.name ?? sid)
                    .join(", ")
                : t("settings.allSites")}
            </TableCell>
            <TableCell>
              <Badge
                className={
                  member.is_active
                    ? "border-transparent bg-emerald-50 text-emerald-700"
                    : "border-transparent bg-gray-50 text-gray-400"
                }
              >
                {member.is_active ? t("settings.active") : t("settings.inactive")}
              </Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {member.last_sign_in_at
                ? formatDate(locale, member.last_sign_in_at)
                : t("settings.never")}
            </TableCell>
            <TableCell className="text-right">
              <EditMemberDialog member={member} roles={roles} sites={sites} headers={headers} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function InvitationsTable({
  invitations,
  sites,
  isLoading,
  headers,
}: {
  invitations: Invitation[];
  sites: Site[];
  isLoading: boolean;
  headers: Record<string, string>;
}) {
  const { locale, t } = useTranslation();
  const queryClient = useQueryClient();

  const revokeMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/v1/invitations/${id}`, {
        method: "DELETE",
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invitations"] });
      toast.success(t("settings.invitationRevoked"));
    },
    onError: showApiError,
  });

  const resendMutation = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${API_URL}/api/v1/invitations/${id}/resend`, {
        method: "POST",
        headers,
      });
      if (!res.ok) throw await apiError(t, res);
    },
    onSuccess: () => toast.success(t("settings.invitationResent")),
    onError: showApiError,
  });

  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(2)].map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 py-12 text-center text-muted-foreground">
        <p className="text-sm">{t("settings.noInvitations")}</p>
        <p className="text-xs">{t("settings.noInvitationsHint")}</p>
      </div>
    );
  }

  const statusBadge = (status: string) => {
    switch (status) {
      case "pending":
        return (
          <Badge className="border-transparent bg-orange-50 text-orange-700">
            {t("settings.inviteStatusPending")}
          </Badge>
        );
      case "accepted":
        return (
          <Badge className="border-transparent bg-emerald-50 text-emerald-700">
            {t("settings.inviteStatusAccepted")}
          </Badge>
        );
      case "expired":
        return (
          <Badge className="border-transparent bg-gray-100 text-gray-600">
            {t("settings.inviteStatusExpired")}
          </Badge>
        );
      case "revoked":
        return (
          <Badge className="border-transparent bg-red-50 text-red-700">
            {t("settings.inviteStatusRevoked")}
          </Badge>
        );
      default:
        return <Badge className="border-transparent bg-gray-100 text-gray-600">{status}</Badge>;
    }
  };

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("settings.columnEmail")}</TableHead>
          <TableHead>{t("settings.columnRole")}</TableHead>
          <TableHead>{t("settings.columnSites")}</TableHead>
          <TableHead>{t("settings.columnStatus")}</TableHead>
          <TableHead>{t("settings.columnSent")}</TableHead>
          <TableHead>{t("settings.columnExpires")}</TableHead>
          <TableHead className="text-right">{t("settings.columnActions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {invitations.map((inv) => (
          <TableRow key={inv.id}>
            <TableCell className="font-medium">{inv.email}</TableCell>
            <TableCell>
              <Badge variant="secondary">{inv.role_name ?? "—"}</Badge>
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {inv.site_ids && inv.site_ids.length > 0
                ? inv.site_ids.map((sid) => sites.find((s) => s.id === sid)?.name ?? sid).join(", ")
                : t("settings.allSites")}
            </TableCell>
            <TableCell>{statusBadge(inv.status)}</TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDate(locale, inv.created_at)}
            </TableCell>
            <TableCell className="text-xs text-muted-foreground">
              {formatDate(locale, inv.expires_at)}
            </TableCell>
            <TableCell className="text-right">
              {inv.status === "pending" && (
                <div className="flex justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => resendMutation.mutate(inv.id)}
                    disabled={resendMutation.isPending}
                  >
                    {t("settings.resend")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-destructive hover:text-destructive"
                    onClick={() => {
                      if (confirm(t("settings.revokeConfirm", { email: inv.email }))) {
                        revokeMutation.mutate(inv.id);
                      }
                    }}
                    disabled={revokeMutation.isPending}
                  >
                    {t("settings.revoke")}
                  </Button>
                </div>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
